// Lightweight, flexible seasonal orientation.
//
// Seasonality is intentionally NOT a schema field on items — see the product
// decision to keep this flexible. Instead we infer a season from an item's
// free-text tags and its name, so users don't have to pre-tag everything.
// Any explicit tag (e.g. "winter") always wins over name-keyword inference.
/**
 * The seasonal buckets we surface for orientation. This is a superset-friendly
 * list: it mirrors the calendar seasons plus "holiday", which are the buckets
 * that are actionable when deciding whether an unused item is really clutter
 * or just out of season. "moving"/"guest" from the boxes `seasons` enum are
 * deliberately omitted here — they're storage contexts, not calendar seasons.
 */
export const SEASON_KEYS = ["winter", "spring", "summer", "fall", "holiday"];
export const SEASON_LABELS = {
    winter: "Winter",
    spring: "Spring",
    summer: "Summer",
    fall: "Fall",
    holiday: "Holiday",
};
// Keyword hints matched against an item's name (case-insensitive, word-ish).
// Kept deliberately small and high-precision — false positives are worse than
// misses here, since a wrong season label undermines trust in the whole lens.
const NAME_KEYWORDS = {
    winter: [
        "winter", "snow", "ski", "sled", "coat", "parka", "mitten", "glove",
        "scarf", "beanie", "boot", "thermal", "sweater", "fleece", "heater",
        "shovel", "ice", "flannel",
    ],
    spring: ["spring", "raincoat", "rain jacket", "umbrella", "galoshes", "allergy", "seedling", "garden"],
    summer: [
        "summer", "swim", "bikini", "trunks", "sandal", "flip flop", "flip-flop",
        "beach", "sunscreen", "sunglass", "shorts", "tank top", "cooler", "fan",
        "pool", "sun hat",
    ],
    fall: ["fall", "autumn", "rake", "jacket", "hoodie", "cardigan"],
    holiday: [
        "holiday", "christmas", "xmas", "hanukkah", "halloween", "costume",
        "ornament", "wreath", "garland", "tinsel", "advent", "menorah", "easter",
        "thanksgiving", "gift wrap", "wrapping paper", "string lights",
    ],
};
// Explicit tags we treat as a season signal. Tags are stored lowercased.
const TAG_ALIASES = {
    winter: ["winter", "cold-weather", "cold weather"],
    spring: ["spring"],
    summer: ["summer", "warm-weather", "warm weather"],
    fall: ["fall", "autumn"],
    holiday: ["holiday", "holidays", "seasonal-decor", "festive"],
};
function matchKeyword(haystack, needle) {
    // Multi-word needles: plain substring. Single words: guard against matching
    // inside larger words (e.g. "ice" inside "service") with a rough boundary check.
    if (needle.includes(" ") || needle.includes("-"))
        return haystack.includes(needle);
    const idx = haystack.indexOf(needle);
    if (idx === -1)
        return false;
    const before = idx === 0 ? " " : haystack[idx - 1];
    const after = idx + needle.length >= haystack.length ? " " : haystack[idx + needle.length];
    const isBoundary = (c) => !/[a-z0-9]/.test(c);
    return isBoundary(before) && isBoundary(after);
}
/**
 * Infer the set of seasons an item likely belongs to. Returns an array because
 * an item can legitimately map to more than one (e.g. a "jacket" reads as both
 * fall and winter). Explicit tags are authoritative; name keywords supplement.
 */
export function inferSeasons(name, tags = []) {
    const result = new Set();
    const normalizedTags = tags.map((t) => t.trim().toLowerCase());
    for (const key of SEASON_KEYS) {
        // 1) Explicit tag match (authoritative)
        if (TAG_ALIASES[key].some((alias) => normalizedTags.includes(alias))) {
            result.add(key);
            continue;
        }
        // 2) Name keyword inference
        const haystack = ` ${name.toLowerCase().trim()} `;
        if (NAME_KEYWORDS[key].some((kw) => matchKeyword(haystack, kw))) {
            result.add(key);
        }
    }
    return SEASON_KEYS.filter((k) => result.has(k));
}
/** Convenience: the primary (first) inferred season, or null if none. */
export function primarySeason(name, tags = []) {
    return inferSeasons(name, tags)[0] ?? null;
}
//# sourceMappingURL=season-utils.js.map